<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex gap-1 mb-4 border-b border-gray-200">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition"
        :class="mainTab === 'published'
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-500 hover:text-gray-800'"
        @click="mainTab = 'published'"
      >
        {{ $t('smm.media.tab_published') }}
      </button>
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition"
        :class="mainTab === 'generated'
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-500 hover:text-gray-800'"
        @click="mainTab = 'generated'"
      >
        {{ $t('smm.media.tab_generated') }}
      </button>
    </div>

    <div v-show="mainTab === 'published'">
    <div class="flex flex-wrap justify-end gap-2 mb-4">
      <button
        type="button"
        @click="showEtsyModal = true"
        class="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm"
      >
        {{ $t('smm.media.etsy_import') }}
      </button>
      <button
        type="button"
        @click="openForm()"
        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
      >
        {{ $t('smm.media.add') }}
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-3 mb-4">
      <nav class="flex flex-wrap gap-2 flex-1 min-w-0 overflow-x-auto pb-1">
        <label
          class="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap border cursor-pointer select-none transition"
          :class="isAllShopsFilter
            ? 'bg-gray-900 text-white border-gray-900'
            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'"
        >
          <input
            type="checkbox"
            class="h-3.5 w-3.5 rounded border-gray-300"
            :checked="isAllShopsFilter"
            @change="toggleAllShopsFilter"
          />
          {{ $t('smm.media.filter_all') }}
        </label>
        <label
          v-for="shop in shops"
          :key="shop.id"
          class="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap border cursor-pointer select-none transition"
          :class="isShopFilterSelected(shop.id) ? '' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'"
          :style="isShopFilterSelected(shop.id) ? shopChipStyle(shop.color) : null"
        >
          <input
            type="checkbox"
            class="h-3.5 w-3.5 rounded border-gray-300"
            :checked="isShopFilterSelected(shop.id)"
            @change="toggleShopFilter(shop.id)"
          />
          {{ shop.name }}
        </label>
        <label
          class="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap border cursor-pointer select-none transition"
          :class="shopFilterUnknown
            ? 'bg-gray-900 text-white border-gray-900'
            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'"
        >
          <input
            type="checkbox"
            class="h-3.5 w-3.5 rounded border-gray-300"
            :checked="shopFilterUnknown"
            @change="toggleUnknownFilter"
          />
          {{ $t('smm.media.filter_unknown') }}
        </label>
      </nav>

      <div class="flex items-center gap-2 w-full sm:w-auto sm:ml-auto shrink-0">
        <input
          v-model="searchInput"
          type="search"
          enterkeyhint="search"
          :placeholder="$t('smm.media.search_placeholder')"
          class="flex-1 sm:w-56 md:w-64 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
          @keydown.enter.prevent="applySearch"
        />
        <button
          type="button"
          class="shrink-0 px-3 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800"
          @click="applySearch"
        >
          {{ $t('smm.media.search_button') }}
        </button>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2 mb-4">
      <button
        type="button"
        class="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
        @click="selectAllLoaded"
      >
        {{ $t('smm.media.select_all') }}
      </button>
      <button
        type="button"
        class="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
        @click="clearSelection"
      >
        {{ $t('smm.media.deselect_all') }}
      </button>
      <div class="relative">
        <select
          v-model="batchAction"
          class="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white pr-8"
          :disabled="!selectedIds.length || batchBusy"
          @change="runBatchAction"
        >
          <option value="">{{ $t('smm.media.with_selected') }}{{ selectedIds.length ? ` (${selectedIds.length})` : '' }}</option>
          <option value="activate">{{ $t('smm.media.batch_activate') }}</option>
          <option value="deactivate">{{ $t('smm.media.batch_deactivate') }}</option>
          <option value="assign_shop">{{ $t('smm.media.batch_assign_shop') }}</option>
          <option value="save_images">{{ $t('smm.media.batch_save_images') }}</option>
          <option value="delete">{{ $t('smm.media.batch_delete') }}</option>
        </select>
      </div>
      <div class="ml-auto text-sm text-gray-500 whitespace-nowrap">
        {{ $t('smm.media.list_stats', {
          selected: selectedIds.length,
          shown: items.length,
          total: totalCount,
        }) }}
      </div>
    </div>

    <div v-if="loading && !items.length" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>
    <div v-else-if="loadError && !items.length" class="p-8 text-center text-red-600 text-sm">{{ loadError }}</div>
    <div v-else-if="!items.length" class="p-8 text-center text-gray-500 bg-white rounded-lg shadow-md">
      {{ hasActiveShopFilter ? $t('smm.media.empty_shop') : $t('smm.media.empty') }}
    </div>
    <div
      v-else
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    >
      <article
        v-for="item in items"
        :key="item.id"
        class="relative bg-white rounded-lg shadow-md border border-gray-100 p-3 flex flex-col hover:shadow-lg hover:border-gray-200 transition"
        :class="{ 'opacity-60': item.is_active === false }"
      >
        <button
          type="button"
          class="absolute top-2 right-2 z-20 flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
          :title="$t('common.delete')"
          :aria-label="$t('common.delete')"
          @click.stop="remove(item)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>

        <div class="mb-2 overflow-hidden pr-7">
          <div
            class="relative float-left w-[100px] h-[100px] mr-3 mb-1 rounded-md overflow-hidden bg-gray-100 border cursor-pointer"
            @click="openForm(item, 'media')"
          >
            <div
              class="absolute top-1 left-1 z-10 flex items-center justify-center w-5 h-5 rounded bg-white/90 border border-gray-300 shadow-sm cursor-pointer select-none"
              @mousedown.stop="onSelectMouseDown($event)"
              @click.stop.prevent="onSelectClick(item, $event)"
            >
              <input
                type="checkbox"
                class="h-3.5 w-3.5 pointer-events-none"
                :checked="isSelected(item.id)"
                tabindex="-1"
                readonly
              />
            </div>
            <img
              v-if="coverImage(item)"
              :src="coverImage(item).url"
              :alt="item.title"
              class="w-full h-full object-cover pointer-events-none"
            />
            <div
              v-else-if="coverVideo(item)"
              class="w-full h-full flex items-center justify-center bg-gray-800 text-white text-[10px] pointer-events-none"
            >
              {{ $t('smm.media.mp4_badge') }}
            </div>
            <div v-else class="w-full h-full flex items-center justify-center text-gray-400 text-xs pointer-events-none">—</div>
          </div>

          <h3
            class="font-medium text-gray-900 text-sm leading-snug mb-1 underline underline-offset-2 decoration-gray-300 hover:decoration-gray-700 cursor-pointer"
            :title="item.title"
            @click="openForm(item, 'main')"
          >
            {{ truncateTitle(item.title) }}
          </h3>

          <div class="flex flex-wrap gap-1 mb-1">
            <span
              class="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full border"
              :class="item.shop ? '' : 'bg-gray-50 text-gray-500 border-gray-200'"
              :style="item.shop ? shopChipStyle(item.shop.color) : null"
            >
              {{ item.shop?.name || $t('smm.media.no_shop') }}
            </span>
            <span
              v-if="item.is_active === false"
              class="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full border bg-red-50 text-red-600 border-red-300"
            >
              {{ $t('smm.media.inactive_badge') }}
            </span>
          </div>

        </div>

        <div class="mt-2 mb-2 clear-both flex items-center gap-2 text-xs text-gray-400">
          <span
            class="inline-flex items-center gap-1"
            :title="$t('smm.media.media_count', { n: item.files?.length || 0 })"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
            </svg>
            {{ item.files?.length || 0 }}
          </span>
          <button
            v-if="!(item.tags?.length)"
            type="button"
            class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 disabled:no-underline"
            :disabled="isGeneratingTagsFor(item.id) || !item.title"
            :title="$t('smm.media.tags_generate_quick')"
            @click.stop="generateTagsForItem(item)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 shrink-0" :class="{ 'animate-pulse': isGeneratingTagsFor(item.id) }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" />
            </svg>
            {{ isGeneratingTagsFor(item.id) ? '…' : 0 }}
          </button>
          <span
            v-else
            class="inline-flex items-center gap-1"
            :class="(item.tags?.length || 0) > 6 ? 'text-green-600' : ''"
            :title="$t('smm.media.tags_count', { n: item.tags?.length || 0 })"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" />
            </svg>
            {{ item.tags?.length || 0 }}
          </span>

          <button
            v-if="item.link"
            type="button"
            class="ml-auto shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            :title="copiedLinkKey === 'card-' + item.id ? $t('common.copied') : $t('common.copy_link')"
            :aria-label="$t('common.copy_link')"
            @click.stop="copyLink(item.link, 'card-' + item.id)"
          >
            <svg
              v-if="copiedLinkKey === 'card-' + item.id"
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="1.75"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m15.112-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
          </button>
        </div>
      </article>
    </div>

    <div ref="sentinel" class="h-8"></div>
    <div v-if="loadingMore" class="text-center text-sm text-gray-500 py-4">
      {{ $t('smm.media.loading_more') }}
    </div>
    </div>

    <div v-show="mainTab === 'generated'">
      <div class="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-800">{{ $t('smm.etsyGen.panel_title') }}</h2>
          <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.etsyGen.panel_subtitle') }}</p>
        </div>
        <button
          type="button"
          class="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm"
          @click="openNewListing"
        >
          {{ $t('smm.etsyGen.new_listing') }}
        </button>
      </div>

      <div v-if="etsyListingsLoading" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>
      <div v-else-if="!etsyListings.length" class="p-8 text-center text-gray-500 bg-white rounded-lg shadow-md">
        {{ $t('smm.etsyGen.panel_empty') }}
      </div>
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <article
          v-for="listing in etsyListings"
          :key="listing.id"
          class="bg-white rounded-lg shadow-md border border-gray-100 p-4 flex gap-3 hover:border-gray-200 hover:shadow-lg transition"
        >
          <button
            v-if="listing.image_urls && listing.image_urls.length"
            type="button"
            class="block w-24 h-24 shrink-0 rounded-md overflow-hidden bg-gray-100 border border-gray-100"
            @click="openListing(listing)"
          >
            <img :src="listing.image_urls[0]" :alt="listing.title || ''" class="w-full h-full object-cover" />
          </button>
          <div class="flex flex-col min-w-0 flex-1">
            <div class="flex items-start justify-between gap-2 mb-1">
              <button
                type="button"
                class="font-semibold text-gray-800 hover:text-gray-950 text-left leading-snug min-w-0"
                @click="openListing(listing)"
              >
                {{ listing.title || $t('smm.etsyGen.list_untitled') }}
              </button>
              <button
                type="button"
                class="shrink-0 flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                :title="$t('common.delete')"
                @click="deleteListing(listing)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
            <p v-if="listing.description" class="text-xs text-gray-500 line-clamp-2 mb-2">{{ listing.description }}</p>
            <div class="mt-auto flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400">
              <span v-if="listing.social_network" class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                {{ listing.social_network.name }}
              </span>
              <span>{{ formatListingDate(listing.created_at) }}</span>
              <button
                type="button"
                class="ml-auto text-gray-600 hover:text-gray-900"
                @click="openListing(listing)"
              >
                {{ $t('smm.etsyGen.open') }}
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>

    <etsy-listing-modal
      :show="showEtsyListingModal"
      :listing="selectedListing"
      @close="showEtsyListingModal = false"
      @saved="onListingSaved"
    />

    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto"
      @click.self="closeForm"
    >
      <div class="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-5xl p-4 sm:p-6 my-0 sm:my-8 max-h-[92vh] overflow-y-auto">
        <div class="flex items-start justify-between gap-3 mb-3">
          <h3 class="font-semibold text-lg text-gray-900">
            {{ editing ? $t('smm.media.edit_title') : $t('smm.media.new_title') }}
          </h3>
          <button
            type="button"
            class="shrink-0 text-gray-400 hover:text-gray-700 text-xl leading-none px-1"
            :aria-label="$t('common.close')"
            @click="closeForm"
          >
            ×
          </button>
        </div>

        <div class="flex gap-1 mb-4 border-b border-gray-200">
          <button
            type="button"
            class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition"
            :class="formTab === 'main'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'"
            @click="formTab = 'main'"
          >
            {{ $t('smm.media.tab_main') }}
          </button>
          <button
            type="button"
            class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition"
            :class="formTab === 'media'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'"
            @click="switchFormTab('media')"
          >
            {{ $t('smm.media.tab_media') }}
            <span class="text-gray-400 font-normal">({{ formMediaCount }})</span>
          </button>
        </div>

        <form @submit.prevent="save" class="space-y-3">
          <div v-show="formTab === 'main'" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] gap-4 items-start">
              <div class="w-[200px] h-[200px] max-w-full rounded-lg overflow-hidden border bg-gray-100 shrink-0">
                <img
                  v-if="formPreview?.type === 'image'"
                  :src="formPreview.url"
                  :alt="form.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else-if="formPreview?.type === 'video'"
                  class="relative w-full h-full bg-gray-800"
                >
                  <video
                    :src="formPreview.url"
                    class="w-full h-full object-cover pointer-events-none"
                    muted
                    playsinline
                    preload="metadata"
                  />
                  <button
                    type="button"
                    class="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40"
                    @click="openVideoPreview({ src: formPreview.url, title: form.title || $t('smm.media.video') })"
                  >
                    <span class="w-10 h-10 rounded-full bg-white/95 text-gray-900 flex items-center justify-center shadow pl-0.5">▶</span>
                  </button>
                </div>
                <div v-else class="w-full h-full flex items-center justify-center text-gray-400 text-sm px-3 text-center">
                  {{ $t('smm.media.preview_empty') }}
                </div>
              </div>

              <div class="space-y-3 min-w-0">
                <div>
                  <label class="text-sm text-gray-600">{{ $t('common.name') }}</label>
                  <input v-model="form.title" required class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm" />
                </div>
                <div>
                  <label class="text-sm text-gray-600">{{ $t('common.link') }}</label>
                  <div class="flex gap-2 mt-1">
                    <input
                      v-model="form.link"
                      type="url"
                      :placeholder="$t('common.url_placeholder')"
                      class="flex-1 min-w-0 border rounded-lg p-2.5 text-base sm:text-sm"
                    />
                    <button
                      type="button"
                      class="shrink-0 px-2.5 rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40"
                      :disabled="!form.link.trim()"
                      :title="copiedLinkKey === 'form' ? $t('common.copied') : $t('common.copy_link')"
                      :aria-label="$t('common.copy_link')"
                      @click="copyLink(form.link, 'form')"
                    >
                      <svg v-if="copiedLinkKey === 'form'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div class="space-y-3 min-w-0">
                <div>
                  <label class="text-sm text-gray-600">{{ $t('smm.media.shop') }}</label>
                  <select v-model="form.shop_id" required class="w-full border rounded-lg p-2.5 mt-1 text-base sm:text-sm">
                    <option disabled value="">{{ $t('common.select_placeholder') }}</option>
                    <option
                      v-for="shop in shops"
                      :key="'form-shop-' + shop.id"
                      :value="String(shop.id)"
                    >
                      {{ shop.name }}
                    </option>
                  </select>
                  <p v-if="!shops.length" class="text-xs text-red-600 mt-1">{{ $t('smm.media.shops_missing') }}</p>
                </div>
                <label class="inline-flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer mt-1">
                  <input v-model="form.is_active" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
                  {{ $t('smm.media.is_active') }}
                </label>
              </div>
            </div>

            <div>
              <div class="flex flex-wrap items-center justify-between gap-2">
                <label class="text-sm text-gray-600">{{ $t('smm.media.tags') }}</label>
                <button
                  type="button"
                  class="text-sm px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                  :disabled="tagGenerating || !form.title.trim()"
                  @click="generateTags"
                >
                  {{ tagGenerating ? $t('smm.media.tags_generating') : $t('smm.media.tags_generate') }}
                </button>
              </div>
              <div class="flex flex-wrap gap-1.5 mt-1.5 min-h-[1.75rem]">
                <span
                  v-for="tag in selectedFormTags"
                  :key="'form-tag-' + tag.id"
                  class="inline-flex items-center gap-1 text-xs font-medium pl-2 pr-1 py-0.5 rounded-full border"
                  :class="tag.is_base
                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                    : 'bg-gray-50 text-gray-700 border-gray-200'"
                >
                  {{ tag.name }}
                  <span class="opacity-70 font-normal">({{ tag.media_contents_count ?? 0 }})</span>
                  <button
                    type="button"
                    class="w-4 h-4 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 leading-none"
                    :title="$t('common.delete')"
                    :aria-label="$t('common.delete')"
                    @click="removeFormTag(tag.id)"
                  >
                    ×
                  </button>
                </span>
                <span v-if="!selectedFormTags.length" class="text-xs text-gray-400 self-center">
                  {{ $t('smm.media.tags_empty') }}
                </span>
              </div>

              <div
                v-if="tagPreview.length"
                class="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3 space-y-2"
              >
                <p class="text-sm text-blue-900 font-medium">{{ $t('smm.media.tags_preview_title') }}</p>
                <p class="text-xs text-blue-800/80">{{ $t('smm.media.tags_preview_hint') }}</p>
                <div class="flex flex-wrap gap-1.5">
                  <label
                    v-for="(item, index) in tagPreview"
                    :key="'preview-tag-' + index"
                    class="inline-flex items-center gap-1.5 text-xs font-medium pl-2 pr-2 py-1 rounded-full border cursor-pointer select-none"
                    :class="item.selected
                      ? 'bg-white text-blue-800 border-blue-300'
                      : 'bg-transparent text-blue-400 border-blue-200 line-through'"
                  >
                    <input v-model="item.selected" type="checkbox" class="h-3 w-3 rounded border-blue-300" />
                    {{ item.name }}
                  </label>
                </div>
                <div class="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    class="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    :disabled="tagBusy || !tagPreview.some((t) => t.selected)"
                    @click="applyTagPreview"
                  >
                    {{ $t('smm.media.tags_preview_apply') }}
                  </button>
                  <button
                    type="button"
                    class="px-3 py-1.5 text-sm rounded-lg border border-blue-200 text-blue-800 hover:bg-white"
                    :disabled="tagBusy"
                    @click="cancelTagPreview"
                  >
                    {{ $t('smm.media.tags_preview_cancel') }}
                  </button>
                </div>
              </div>

              <div class="flex gap-2 mt-2">
                <input
                  v-model="tagInput"
                  type="text"
                  list="media-tag-suggestions"
                  :placeholder="$t('smm.media.tags_placeholder')"
                  class="flex-1 border rounded-lg p-2.5 text-base sm:text-sm"
                  @keydown.enter.prevent="addTagFromInput"
                />
                <datalist id="media-tag-suggestions">
                  <option
                    v-for="tag in availableFormTags"
                    :key="'suggest-tag-' + tag.id"
                    :value="tag.name"
                  />
                </datalist>
                <button
                  type="button"
                  class="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
                  :disabled="!tagInput.trim() || tagBusy"
                  @click="addTagFromInput"
                >
                  {{ $t('smm.media.tag_add') }}
                </button>
              </div>
            </div>

            <div>
              <label class="text-sm text-gray-600">{{ $t('smm.media.short_description') }}</label>
              <textarea v-model="form.description" rows="5" class="w-full border rounded-lg p-2.5 mt-1 resize-y min-h-[7rem] text-base sm:text-sm" />
            </div>
          </div>

          <div v-show="formTab === 'media'" class="space-y-3">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="text-xs text-gray-500">{{ $t('smm.media.files_drag_hint') }}</p>
              <button
                type="button"
                class="text-sm px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                :disabled="!selectedSaveFileIds.length || saveImagesBusy"
                @click="saveSelectedProductImages"
              >
                {{ saveImagesBusy
                  ? $t('smm.media.save_images_busy')
                  : $t('smm.media.save_images_selected', { n: selectedSaveFileIds.length || 0 }) }}
              </button>
            </div>
            <div
              class="grid gap-3"
              style="grid-template-columns: repeat(auto-fill, minmax(128px, 128px));"
            >
              <media-upload-panel
                ref="uploadPanel"
                v-model="pendingFiles"
                compact
                @play-video="openVideoPreview"
              />
              <div
                v-for="(file, index) in existingFiles"
                :key="'existing-file-' + file.id"
                class="relative rounded-lg overflow-hidden border aspect-square group bg-gray-100 cursor-grab active:cursor-grabbing w-[128px] h-[128px]"
                :class="isSaveFileSelected(file.id) ? 'ring-2 ring-blue-500' : ''"
                draggable="true"
                @dragstart="onExistingFileDragStart(index, $event)"
                @dragover.prevent
                @drop.prevent="onExistingFileDrop(index)"
                @click="file.type === 'image' && toggleSaveFile(file.id)"
              >
                <img
                  v-if="file.type === 'image'"
                  :src="file.url"
                  :alt="file.original_name || form.title"
                  class="w-full h-full object-cover pointer-events-none"
                  draggable="false"
                />
                <template v-else>
                  <video
                    :src="file.url"
                    class="w-full h-full object-cover pointer-events-none"
                    muted
                    playsinline
                    preload="metadata"
                  />
                  <button
                    type="button"
                    class="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 transition"
                    :title="$t('smm.media.play')"
                    @click.stop="openVideoPreview({ src: file.url, title: file.original_name || $t('smm.media.video') })"
                  >
                    <span class="w-8 h-8 rounded-full bg-white/95 text-gray-900 flex items-center justify-center shadow pl-px text-sm">
                      ▶
                    </span>
                  </button>
                </template>

                <span
                  v-if="file.type === 'image'"
                  class="absolute top-2 left-2 z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 shadow-sm pointer-events-none"
                  :class="isSaveFileSelected(file.id)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white/90 border-gray-300 text-transparent'"
                  aria-hidden="true"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </span>
                <span class="absolute top-2 left-12 z-10 text-[10px] px-1.5 py-0.5 rounded bg-black/55 text-white pointer-events-none"
                  :class="file.type !== 'image' ? '!left-1.5 !top-1.5' : ''"
                >
                  {{ index + 1 }}
                </span>
                <button
                  v-if="editing"
                  type="button"
                  class="absolute top-1.5 right-1.5 z-10 w-7 h-7 rounded-full bg-red-600 text-white text-sm leading-none opacity-90 sm:opacity-0 sm:group-hover:opacity-100"
                  @click.stop="deleteExistingFile(file)"
                  @mousedown.stop
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <p v-if="formError" class="text-sm text-red-600 whitespace-pre-line">{{ formError }}</p>
          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
            <button type="button" @click="closeForm" class="w-full sm:w-auto px-4 py-2.5 text-gray-600 rounded-lg hover:bg-gray-50">
              {{ $t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              :disabled="saving"
            >
              {{ saving ? $t('common.saving') : (editing ? $t('common.save') : $t('common.add')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <etsy-import-modal
      :show="showEtsyModal"
      :shops="shops"
      @close="showEtsyModal = false"
      @saved="reloadFromStart"
    />

    <video-preview-modal
      :show="videoPreview.show"
      :src="videoPreview.src"
      :title="videoPreview.title"
      @close="closeVideoPreview"
    />
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'
import MediaUploadPanel from '@/components/smm/MediaUploadPanel.vue'
import EtsyImportModal from '@/components/smm/EtsyImportModal.vue'
import EtsyListingModal from '@/components/smm/EtsyListingModal.vue'
import VideoPreviewModal from '@/components/smm/VideoPreviewModal.vue'

const PAGE_SIZE = 32
const FILTERS_STORAGE_KEY = 'smm.media.listFilters.v1'

export default {
  components: {
    SmmSubNav,
    MediaUploadPanel,
    EtsyImportModal,
    EtsyListingModal,
    VideoPreviewModal,
  },
  data: () => ({
    mainTab: 'published',
    showEtsyListingModal: false,
    selectedListing: null,
    etsyListings: [],
    etsyListingsLoading: false,
    items: [],
    shops: [],
    shopFilterIds: [],
    shopFilterUnknown: true,
    searchInput: '',
    searchQuery: '',
    page: 1,
    hasMore: true,
    totalCount: 0,
    loading: true,
    loadingMore: false,
    loadError: null,
    selectedIds: [],
    selectedMap: {},
    lastSelectedIndex: null,
    batchAction: '',
    batchBusy: false,
    copiedLinkKey: null,
    copiedLinkTimer: null,
    selectedSaveFileIds: [],
    saveImagesBusy: false,
    showModal: false,
    showEtsyModal: false,
    videoPreview: { show: false, src: '', title: '' },
    editing: null,
    saving: false,
    formError: null,
    pendingFiles: [],
    formTab: 'main',
    existingFileDragIndex: null,
    filesReorderBusy: false,
    tags: [],
    tagInput: '',
    tagBusy: false,
    tagGenerating: false,
    tagPreview: [],
    tagGeneratingMap: {},
    form: {
      title: '',
      link: '',
      description: '',
      shop_id: '',
      tag_ids: [],
      is_active: true,
    },
    observer: null,
  }),
  mounted() {
    Promise.all([this.loadShops(), this.loadTags()]).then(() => this.reloadFromStart())
    this.$nextTick(() => this.setupObserver())
  },
  watch: {
    mainTab(tab) {
      if (tab === 'generated' && !this.etsyListings.length && !this.etsyListingsLoading) {
        this.loadEtsyListings()
      }
    },
  },
  beforeUnmount() {
    this.observer?.disconnect()
  },
  computed: {
    existingFiles() {
      return this.editing?.files ?? []
    },
    formMediaCount() {
      return (this.existingFiles.length || 0) + (this.pendingFiles.length || 0)
    },
    formPreview() {
      const firstExisting = this.existingFiles[0]
      if (firstExisting) {
        return { type: firstExisting.type, url: firstExisting.url }
      }
      const firstPending = this.pendingFiles[0]
      if (firstPending) {
        return {
          type: firstPending.kind === 'video' ? 'video' : 'image',
          url: firstPending.previewUrl,
        }
      }
      return null
    },
    selectedFormTags() {
      const ids = new Set((this.form.tag_ids || []).map(Number))
      return this.tags.filter((t) => ids.has(Number(t.id)))
    },
    availableFormTags() {
      const ids = new Set((this.form.tag_ids || []).map(Number))
      return this.tags.filter((t) => !ids.has(Number(t.id)))
    },
    isAllShopsFilter() {
      const allShopsSelected = this.shops.length === 0
        || (
          this.shopFilterIds.length === this.shops.length
          && this.shops.every((s) => this.shopFilterIds.includes(Number(s.id)))
        )
      return allShopsSelected && this.shopFilterUnknown
    },
    hasActiveShopFilter() {
      return !this.isAllShopsFilter
        && (this.shopFilterIds.length > 0 || this.shopFilterUnknown)
    },
  },
  methods: {
    openNewListing() {
      this.selectedListing = null
      this.showEtsyListingModal = true
    },
    openListing(listing) {
      this.selectedListing = listing
      this.showEtsyListingModal = true
    },
    async loadEtsyListings() {
      this.etsyListingsLoading = true
      try {
        const { data } = await axios.get('/api/v1/smm/etsy-listings')
        this.etsyListings = Array.isArray(data?.data) ? data.data : []
      } catch (e) {
        this.etsyListings = []
      } finally {
        this.etsyListingsLoading = false
      }
    },
    onListingSaved() {
      this.loadEtsyListings()
    },
    async deleteListing(listing) {
      if (!confirm(this.$t('smm.etsyGen.delete_confirm'))) return
      try {
        await axios.delete(`/api/v1/smm/etsy-listings/${listing.id}`)
        this.etsyListings = this.etsyListings.filter((l) => l.id !== listing.id)
      } catch (e) {
        alert(this.formatValidationError(e) || this.$t('common.delete_error'))
      }
    },
    formatListingDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString(this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
    async loadShops() {
      try {
        const { data } = await axios.get('/api/v1/smm/shops')
        this.shops = (data.data ?? []).map((s) => ({
          ...s,
          id: Number(s.id),
        }))
        this.restoreListFilters()
      } catch (e) {
        this.shops = []
        this.shopFilterIds = []
        this.shopFilterUnknown = true
        console.error('Failed to load shops', e)
      }
    },
    readStoredListFilters() {
      try {
        const raw = localStorage.getItem(FILTERS_STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null
        return parsed
      } catch (e) {
        return null
      }
    },
    persistListFilters() {
      try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
          shopFilterIds: this.shopFilterIds.map(Number),
          shopFilterUnknown: !!this.shopFilterUnknown,
          searchInput: this.searchInput || '',
          searchQuery: this.searchQuery || '',
        }))
      } catch (e) {
        // ignore quota / private mode
      }
    },
    restoreListFilters() {
      const saved = this.readStoredListFilters()
      const shopIds = this.shops.map((s) => Number(s.id))

      if (!saved) {
        this.shopFilterIds = [...shopIds]
        this.shopFilterUnknown = true
        this.searchInput = ''
        this.searchQuery = ''
        return
      }

      const savedIds = Array.isArray(saved.shopFilterIds)
        ? saved.shopFilterIds.map(Number).filter((id) => shopIds.includes(id))
        : []

      // If storage had a selection but all those shops disappeared, fall back to all
      if (savedIds.length || saved.shopFilterUnknown) {
        this.shopFilterIds = savedIds
        this.shopFilterUnknown = !!saved.shopFilterUnknown
      } else if (Array.isArray(saved.shopFilterIds) && saved.shopFilterIds.length === 0 && saved.shopFilterUnknown === false) {
        // Explicitly cleared everything
        this.shopFilterIds = []
        this.shopFilterUnknown = false
      } else {
        this.shopFilterIds = [...shopIds]
        this.shopFilterUnknown = true
      }

      this.searchInput = typeof saved.searchInput === 'string' ? saved.searchInput : ''
      this.searchQuery = typeof saved.searchQuery === 'string'
        ? saved.searchQuery
        : this.searchInput.trim()
    },
    async loadTags() {
      try {
        const { data } = await axios.get('/api/v1/smm/tags')
        this.tags = (data.data ?? []).map((t) => ({
          ...t,
          id: Number(t.id),
          is_base: !!t.is_base,
          media_contents_count: Number(t.media_contents_count ?? 0),
        }))
      } catch (e) {
        this.tags = []
        console.error('Failed to load tags', e)
      }
    },
    removeFormTag(tagId) {
      const id = Number(tagId)
      this.form.tag_ids = (this.form.tag_ids || []).filter((x) => Number(x) !== id)
    },
    async ensureTagByName(name) {
      const normalized = String(name || '').trim()
      if (!normalized) return null

      const existing = this.tags.find(
        (t) => t.name.toLowerCase() === normalized.toLowerCase()
      )
      if (existing) {
        return existing
      }

      const { data } = await axios.post('/api/v1/smm/tags', { name: normalized })
      const tag = {
        ...data.data,
        id: Number(data.data.id),
        is_base: !!data.data.is_base,
        media_contents_count: Number(data.data.media_contents_count ?? 0),
      }
      this.tags = [...this.tags, tag].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      )
      return tag
    },
    async addTagFromInput() {
      const name = this.tagInput.trim()
      if (!name || this.tagBusy) return

      this.tagBusy = true
      try {
        const tag = await this.ensureTagByName(name)
        if (tag && !this.form.tag_ids.map(Number).includes(Number(tag.id))) {
          this.form.tag_ids = [...this.form.tag_ids, Number(tag.id)]
        }
        this.tagInput = ''
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('smm.media.tag_create_error')
      } finally {
        this.tagBusy = false
      }
    },
    async generateTags() {
      if (this.tagGenerating || !this.form.title.trim()) return

      this.tagGenerating = true
      this.formError = null
      try {
        const exclude = this.selectedFormTags.map((t) => t.name)
        const { data } = await axios.post('/api/v1/smm/tags/generate', {
          title: this.form.title,
          description: this.form.description || '',
          exclude,
        })
        const tags = (data.data?.tags ?? [])
          .map((t) => String(t).trim())
          .filter(Boolean)

        if (!tags.length) {
          this.formError = this.$t('smm.media.tags_generate_empty')
          this.tagPreview = []
          return
        }

        this.tagPreview = tags.map((name) => ({ name, selected: true }))
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('smm.media.tags_generate_error')
        this.tagPreview = []
      } finally {
        this.tagGenerating = false
      }
    },
    cancelTagPreview() {
      this.tagPreview = []
    },
    async applyTagPreview() {
      const selected = this.tagPreview.filter((t) => t.selected).map((t) => t.name)
      if (!selected.length || this.tagBusy) return

      this.tagBusy = true
      this.formError = null
      try {
        const nextIds = [...(this.form.tag_ids || []).map(Number)]
        for (const name of selected) {
          const tag = await this.ensureTagByName(name)
          if (tag && !nextIds.includes(Number(tag.id))) {
            nextIds.push(Number(tag.id))
          }
        }
        this.form.tag_ids = nextIds
        this.tagPreview = []
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('smm.media.tag_create_error')
      } finally {
        this.tagBusy = false
      }
    },
    isGeneratingTagsFor(itemId) {
      return !!this.tagGeneratingMap[Number(itemId)]
    },
    async generateTagsForItem(item) {
      const id = Number(item.id)
      if (!item?.title || (item.tags?.length || 0) > 0 || this.tagGeneratingMap[id]) {
        return
      }

      this.tagGeneratingMap = { ...this.tagGeneratingMap, [id]: true }
      try {
        const { data } = await axios.post('/api/v1/smm/tags/generate', {
          title: item.title,
          description: item.description || '',
          exclude: [],
        })
        const names = (data.data?.tags ?? [])
          .map((t) => String(t).trim())
          .filter(Boolean)

        if (!names.length) {
          alert(this.$t('smm.media.tags_generate_empty'))
          return
        }

        const tagIds = []
        const attached = []
        for (const name of names) {
          const tag = await this.ensureTagByName(name)
          if (tag && !tagIds.includes(Number(tag.id))) {
            tagIds.push(Number(tag.id))
            attached.push(tag)
          }
        }

        await axios.post('/api/v1/smm/tags/sync', {
          entity_type: 'media_content',
          entity_id: id,
          tag_ids: tagIds,
        })

        item.tags = attached
        await this.loadTags()
      } catch (e) {
        alert(this.formatValidationError(e) || this.$t('smm.media.tags_generate_error'))
      } finally {
        const next = { ...this.tagGeneratingMap }
        delete next[id]
        this.tagGeneratingMap = next
      }
    },
    setupObserver() {
      if (!this.$refs.sentinel || this.observer) return
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.loadMore()
          }
        },
        { rootMargin: '200px' }
      )
      this.observer.observe(this.$refs.sentinel)
    },
    isShopFilterSelected(shopId) {
      return this.shopFilterIds.includes(Number(shopId))
    },
    parseHexColor(input) {
      const raw = String(input || '').trim()
      if (!raw) return null
      let hex = raw.replace('#', '')
      if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        hex = hex.split('').map((c) => c + c).join('')
      }
      if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        hex: `#${hex.toLowerCase()}`,
      }
    },
    relativeLuminance({ r, g, b }) {
      const channel = (v) => {
        const s = v / 255
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
      }
      return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
    },
    mixToward(rgb, target, amount) {
      return {
        r: Math.round(rgb.r + (target - rgb.r) * amount),
        g: Math.round(rgb.g + (target - rgb.g) * amount),
        b: Math.round(rgb.b + (target - rgb.b) * amount),
      }
    },
    toCssRgb({ r, g, b }) {
      return `rgb(${r}, ${g}, ${b})`
    },
    shopChipStyle(color) {
      const parsed = this.parseHexColor(color) || this.parseHexColor('#111827')
      const luminance = this.relativeLuminance(parsed)
      const textColor = luminance > 0.55 ? '#111827' : '#ffffff'
      // Border: same hue, much darker (or lighter if already near black)
      const borderRgb = luminance > 0.35
        ? this.mixToward(parsed, 0, 0.55)
        : this.mixToward(parsed, 255, 0.35)
      return {
        backgroundColor: parsed.hex,
        color: textColor,
        borderColor: this.toCssRgb(borderRgb),
      }
    },
    applyShopFilter() {
      this.persistListFilters()
      this.clearSelection()
      this.reloadFromStart()
    },
    applySearch() {
      this.searchQuery = this.searchInput.trim()
      this.persistListFilters()
      this.clearSelection()
      this.reloadFromStart()
    },
    toggleAllShopsFilter() {
      if (this.isAllShopsFilter) {
        this.shopFilterIds = []
        this.shopFilterUnknown = false
      } else {
        this.shopFilterIds = this.shops.map((s) => Number(s.id))
        this.shopFilterUnknown = true
      }
      this.applyShopFilter()
    },
    toggleShopFilter(shopId) {
      const id = Number(shopId)
      if (this.shopFilterIds.includes(id)) {
        this.shopFilterIds = this.shopFilterIds.filter((x) => x !== id)
      } else {
        this.shopFilterIds = [...this.shopFilterIds, id]
      }
      this.applyShopFilter()
    },
    toggleUnknownFilter() {
      this.shopFilterUnknown = !this.shopFilterUnknown
      this.applyShopFilter()
    },
    async reloadFromStart() {
      this.page = 1
      this.hasMore = true
      this.items = []
      await this.loadPage(true)
    },
    async loadMore() {
      if (!this.hasMore || this.loading || this.loadingMore) return
      this.page += 1
      await this.loadPage(false)
    },
    async loadPage(replace) {
      if (replace) {
        this.loading = true
      } else {
        this.loadingMore = true
      }
      this.loadError = null
      try {
        if (!this.shopFilterIds.length && !this.shopFilterUnknown) {
          this.items = []
          this.hasMore = false
          this.totalCount = 0
          return
        }

        const params = {
          page: this.page,
          per_page: PAGE_SIZE,
        }
        if (this.hasActiveShopFilter) {
          if (this.shopFilterIds.length) {
            params.shop_ids = this.shopFilterIds
          }
          if (this.shopFilterUnknown) {
            params.include_unknown = 1
          }
        }
        if (this.searchQuery) {
          params.q = this.searchQuery
        }
        const { data } = await axios.get('/api/v1/smm/media-contents', { params })
        const chunk = data.data ?? []
        this.items = replace ? chunk : [...this.items, ...chunk]
        this.hasMore = !!data.meta?.has_more
        this.totalCount = Number(data.meta?.total ?? this.items.length)
        if (!this.hasMore && chunk.length === 0 && this.page > 1) {
          this.page -= 1
        }
      } catch (e) {
        this.loadError = e.response?.data?.message || this.$t('smm.media.load_error')
        if (!replace) this.page -= 1
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => this.setupObserver())
      }
    },
    isSelected(id) {
      return !!this.selectedMap[Number(id)]
    },
    syncSelectedIds() {
      this.selectedIds = Object.keys(this.selectedMap)
        .filter((key) => this.selectedMap[key])
        .map((key) => Number(key))
    },
    onSelectMouseDown(event) {
      // Shift+click otherwise selects text between the two click points
      if (event.shiftKey) {
        event.preventDefault()
      }
    },
    clearDomSelection() {
      const sel = window.getSelection?.()
      if (sel && sel.removeAllRanges) {
        sel.removeAllRanges()
      }
    },
    onSelectClick(item, event) {
      const id = Number(item.id)
      const index = this.items.findIndex((i) => Number(i.id) === id)
      if (index < 0) return

      if (event.shiftKey && this.lastSelectedIndex != null) {
        event.preventDefault()
        this.clearDomSelection()
        const from = Math.min(this.lastSelectedIndex, index)
        const to = Math.max(this.lastSelectedIndex, index)
        const next = { ...this.selectedMap }
        this.items.slice(from, to + 1).forEach((row) => {
          next[Number(row.id)] = true
        })
        this.selectedMap = next
        this.syncSelectedIds()
        return
      }

      const next = { ...this.selectedMap }
      if (next[id]) {
        delete next[id]
      } else {
        next[id] = true
      }
      this.selectedMap = next
      this.syncSelectedIds()
      this.lastSelectedIndex = index
    },
    selectAllLoaded() {
      const next = {}
      this.items.forEach((item) => {
        next[Number(item.id)] = true
      })
      this.selectedMap = next
      this.syncSelectedIds()
      this.lastSelectedIndex = this.items.length ? this.items.length - 1 : null
    },
    clearSelection() {
      this.selectedMap = {}
      this.selectedIds = []
      this.lastSelectedIndex = null
      this.batchAction = ''
    },
    async runBatchAction() {
      const action = this.batchAction
      this.batchAction = ''
      if (!action) return
      if (!this.selectedIds.length) {
        alert(this.$t('smm.media.batch_none'))
        return
      }

      if (action === 'save_images') {
        await this.saveSelectedProductsImages()
        return
      }

      const payload = {
        action,
        ids: this.selectedIds,
      }

      if (action === 'assign_shop') {
        if (!this.shops.length) {
          alert(this.$t('smm.media.shops_missing'))
          return
        }
        const names = this.shops.map((s, i) => `${i + 1}. ${s.name}`).join('\n')
        const pick = prompt(`${this.$t('smm.media.batch_assign_shop_prompt')}:\n${names}`)
        if (!pick) return
        const index = Number(pick) - 1
        const shop = this.shops[index] || this.shops.find((s) => String(s.id) === String(pick))
        if (!shop) {
          alert(this.$t('smm.media.shop_required'))
          return
        }
        payload.shop_id = shop.id
      }

      if (action === 'delete') {
        if (!confirm(this.$t('smm.media.batch_delete_confirm', { n: this.selectedIds.length }))) return
      }

      this.batchBusy = true
      try {
        await axios.post('/api/v1/smm/media-contents/batch', payload)
        this.clearSelection()
        await this.reloadFromStart()
      } catch (e) {
        alert(this.formatValidationError(e) || this.$t('common.error'))
      } finally {
        this.batchBusy = false
      }
    },
    async copyLink(text, key = 'default') {
      const value = String(text || '').trim()
      if (!value) return
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const input = document.createElement('textarea')
          input.value = value
          input.setAttribute('readonly', '')
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.copiedLinkKey = key
        if (this.copiedLinkTimer) clearTimeout(this.copiedLinkTimer)
        this.copiedLinkTimer = setTimeout(() => {
          if (this.copiedLinkKey === key) this.copiedLinkKey = null
        }, 1500)
      } catch (e) {
        alert(this.$t('common.copy_failed'))
      }
    },
    isSaveFileSelected(fileId) {
      return this.selectedSaveFileIds.includes(Number(fileId))
    },
    toggleSaveFile(fileId) {
      const id = Number(fileId)
      if (this.selectedSaveFileIds.includes(id)) {
        this.selectedSaveFileIds = this.selectedSaveFileIds.filter((x) => x !== id)
      } else {
        this.selectedSaveFileIds = [...this.selectedSaveFileIds, id]
      }
    },
    extensionForMime(mime, fallback = 'jpg') {
      const map = {
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/gif': 'gif',
      }
      return map[(mime || '').toLowerCase()] || fallback
    },
    async fetchImageAsFile(url, baseName = 'image') {
      const response = await fetch(url, { credentials: 'same-origin' })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const blob = await response.blob()
      const mime = blob.type || 'image/jpeg'
      const ext = this.extensionForMime(mime)
      const safe = String(baseName).replace(/[^\w\-]+/g, '_').slice(0, 40) || 'image'
      return new File([blob], `${safe}.${ext}`, { type: mime })
    },
    async shareOrDownloadFiles(files) {
      if (!files.length) return

      const canShareFiles = typeof navigator.canShare === 'function'
        && navigator.canShare({ files })

      if (canShareFiles) {
        await navigator.share({ files })
        return
      }

      if (typeof navigator.share === 'function' && files.length === 1) {
        try {
          await navigator.share({ files })
          return
        } catch (e) {
          if (e?.name === 'AbortError') return
        }
      }

      // Desktop / unsupported share: trigger downloads
      for (const file of files) {
        const objectUrl = URL.createObjectURL(file)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = file.name
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      }
    },
    async saveSelectedProductsImages() {
      const selected = this.items.filter((item) => this.selectedIds.includes(Number(item.id)))
      const targets = []
      selected.forEach((item) => {
        const image = (item.files || []).find((f) => f.type === 'image')
        if (image?.url) {
          targets.push({
            url: image.url,
            name: `${item.title || 'product'}-${image.id}`,
          })
        }
      })

      if (!targets.length) {
        alert(this.$t('smm.media.save_images_none'))
        return
      }

      this.batchBusy = true
      this.saveImagesBusy = true
      try {
        const files = []
        for (const target of targets) {
          files.push(await this.fetchImageAsFile(target.url, target.name))
        }
        await this.shareOrDownloadFiles(files)
      } catch (e) {
        if (e?.name !== 'AbortError') {
          alert(this.$t('smm.media.save_images_error'))
        }
      } finally {
        this.batchBusy = false
        this.saveImagesBusy = false
      }
    },
    async saveSelectedProductImages() {
      const filesMeta = this.existingFiles
        .filter((f) => f.type === 'image' && this.isSaveFileSelected(f.id))
        .map((f) => ({
          url: f.url,
          name: f.original_name || `${this.form.title || 'image'}-${f.id}`,
        }))

      if (!filesMeta.length) {
        alert(this.$t('smm.media.save_images_none'))
        return
      }

      this.saveImagesBusy = true
      try {
        const files = []
        for (const meta of filesMeta) {
          files.push(await this.fetchImageAsFile(meta.url, meta.name))
        }
        await this.shareOrDownloadFiles(files)
      } catch (e) {
        if (e?.name !== 'AbortError') {
          alert(this.$t('smm.media.save_images_error'))
        }
      } finally {
        this.saveImagesBusy = false
      }
    },
    coverImage(item) {
      const first = (item.files || [])[0]
      return first?.type === 'image' ? first : null
    },
    coverVideo(item) {
      const first = (item.files || [])[0]
      return first?.type === 'video' ? first : null
    },
    truncateTitle(title, max = 65) {
      const text = String(title || '')
      if (text.length <= max) return text
      return text.slice(0, max).trimEnd() + '…'
    },
    switchFormTab(tab) {
      this.formTab = tab
      if (tab === 'media') {
        this.$nextTick(() => this.$refs.uploadPanel?.focusPasteZone())
      }
    },
    onExistingFileDragStart(index, event) {
      this.existingFileDragIndex = index
      event.dataTransfer.effectAllowed = 'move'
      try {
        event.dataTransfer.setData('text/plain', String(index))
      } catch (_) {
        // ignore
      }
    },
    async onExistingFileDrop(index) {
      if (this.existingFileDragIndex == null || this.existingFileDragIndex === index) {
        this.existingFileDragIndex = null
        return
      }
      if (!this.editing?.files) {
        this.existingFileDragIndex = null
        return
      }

      const next = [...this.editing.files]
      const [moved] = next.splice(this.existingFileDragIndex, 1)
      next.splice(index, 0, moved)
      this.existingFileDragIndex = null
      this.editing.files = next.map((file, sortOrder) => ({
        ...file,
        sort_order: sortOrder,
      }))

      if (this.filesReorderBusy) return
      this.filesReorderBusy = true
      try {
        const { data } = await axios.post(
          `/api/v1/smm/media-contents/${this.editing.id}/files/reorder`,
          { file_ids: this.editing.files.map((f) => f.id) }
        )
        if (data.data?.files) {
          this.editing.files = data.data.files
        }
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('smm.media.files_reorder_error')
      } finally {
        this.filesReorderBusy = false
      }
    },
    async openForm(item = null, tab = 'main') {
      if (!this.shops.length) {
        await this.loadShops()
      }
      await this.loadTags()
      if (item) {
        item.files = [...(item.files || [])].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
        )
      }
      this.editing = item
      const shopId = item
        ? (item.shop_id || item.shop?.id || '')
        : (this.shopFilterIds.length === 1 ? this.shopFilterIds[0] : (this.shops[0]?.id || ''))
      const tagIds = item?.tags?.length
        ? item.tags.map((t) => Number(t.id))
        : []
      this.form = item
        ? {
            title: item.title,
            link: item.link || '',
            description: item.description || '',
            shop_id: shopId === '' ? '' : String(shopId),
            tag_ids: tagIds,
            is_active: item.is_active !== false,
          }
        : {
            title: '',
            link: '',
            description: '',
            shop_id: shopId === '' ? '' : String(shopId),
            tag_ids: [],
            is_active: true,
          }
      this.tagInput = ''
      this.tagPreview = []
      this.pendingFiles = []
      this.selectedSaveFileIds = []
      this.formError = null
      this.showModal = true
      this.switchFormTab(tab === 'media' ? 'media' : 'main')
    },
    closeForm() {
      this.closeVideoPreview()
      this.$refs.uploadPanel?.clear()
      this.showModal = false
      this.editing = null
      this.pendingFiles = []
      this.tagInput = ''
      this.tagPreview = []
      this.formTab = 'main'
      this.existingFileDragIndex = null
      this.selectedSaveFileIds = []
    },
    openVideoPreview({ src, title }) {
      this.videoPreview = { show: true, src, title: title || this.$t('smm.media.video') }
    },
    closeVideoPreview() {
      this.videoPreview = { show: false, src: '', title: '' }
    },
    buildFormData() {
      const fd = new FormData()
      fd.append('title', this.form.title)
      fd.append('shop_id', this.form.shop_id)
      fd.append('is_active', this.form.is_active ? '1' : '0')
      if (this.form.link) fd.append('link', this.form.link)
      if (this.form.description) fd.append('description', this.form.description)
      ;(this.form.tag_ids || []).forEach((id, i) => {
        fd.append(`tag_ids[${i}]`, String(id))
      })
      this.pendingFiles.forEach((item, i) => {
        fd.append(`files[${i}]`, item.file)
      })
      return fd
    },
    formatValidationError(e) {
      const data = e.response?.data
      if (!data) return null
      if (data.message) return data.message
      if (data.errors) return Object.values(data.errors).flat().join('\n')
      return null
    },
    async save() {
      if (!this.form.shop_id) {
        this.formError = this.$t('smm.media.shop_required')
        return
      }
      this.saving = true
      this.formError = null
      try {
        if (this.editing) {
          await axios.patch(`/api/v1/smm/media-contents/${this.editing.id}`, {
            title: this.form.title,
            link: this.form.link || null,
            description: this.form.description || null,
            shop_id: Number(this.form.shop_id),
            tag_ids: this.form.tag_ids || [],
            is_active: !!this.form.is_active,
          })
          if (this.pendingFiles.length) {
            const fd = new FormData()
            this.pendingFiles.forEach((item, i) => fd.append(`files[${i}]`, item.file))
            const { data } = await axios.post(`/api/v1/smm/media-contents/${this.editing.id}/files`, fd)
            if (data.data?.length) {
              this.editing.files = [...(this.editing.files || []), ...data.data]
            }
          }
        } else {
          if (!this.pendingFiles.length) {
            this.formError = this.$t('smm.media.files_required')
            this.formTab = 'media'
            return
          }
          await axios.post('/api/v1/smm/media-contents', this.buildFormData())
        }
        this.closeForm()
        await this.reloadFromStart()
      } catch (e) {
        this.formError = this.formatValidationError(e) || this.$t('common.save_error')
      } finally {
        this.saving = false
      }
    },
    async deleteExistingFile(file) {
      if (!this.editing || !confirm(this.$t('smm.media.delete_file_confirm'))) return
      try {
        await axios.delete(`/api/v1/smm/media-contents/${this.editing.id}/files/${file.id}`)
        this.editing.files = this.editing.files.filter((f) => f.id !== file.id)
        const listItem = this.items.find((i) => Number(i.id) === Number(this.editing.id))
        if (listItem) {
          listItem.files = this.editing.files
        }
      } catch (e) {
        alert(this.formatValidationError(e) || this.$t('common.delete_error'))
      }
    },
    async remove(item) {
      if (!confirm(this.$t('smm.media.delete_confirm', { title: item.title }))) return
      await axios.delete(`/api/v1/smm/media-contents/${item.id}`)
      await this.reloadFromStart()
    },
  },
}
</script>
